import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { ITextBuilder } from "../../Global/Base/Builders/ITextBuilder";
import { DataConverter } from "../../Global/Base/Converters/DataConverter";
import { Event as GlobalEvent } from "../../Global/Base/Events";
import { ValidityStateHelper } from "../UI.ElementarControls/Helpers/ValidityStateHelper";
import { createElement, registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { Control } from "./Control";
import { IBaseEditableControlRights, IValidatable, IValidationResult } from "./ControlInterfaces";
import { IBooleanDetailValueEvent, IBooleanDetailValueEventData, IWidgetResizableEventTarget } from "./IEvent";
import { PopupMenu } from "./PopupMenu";

const logger = LazyLoadingSectionLogger.create(LoggingSection.editableControl);

/**
    * Interface definujici události pro element tsc-control
    */
export interface EditableControl extends IWidgetResizableEventTarget {
    addEventListener(type: "change", listener: (ev: IBooleanDetailValueEvent) => any, useCapture?: boolean): void;
    addEventListener(type: "isediting", listener: (ev: IBooleanDetailValueEvent) => any, useCapture?: boolean): void;
    addEventListener(type: string, listener: EventListenerOrEventListenerObject, useCapture?: boolean): void;
}

export class EditableControl extends Control implements IValidatable, IBaseEditableControlRights {
    public validityChanged = new GlobalEvent<IValidatable, IValidationResult>();
    public errorPopup: PopupMenu;
    /**
     * Obsahuje hodnotu zda uz byla nactena nebo zmenena hodnota nebo ne (kvuli zmene viditelnosti)
     */
    public isValueLoaded: boolean = false;
    /**
     * Kontrola validnosti pole na gui
     */
    public isGuiValid: boolean = true;
    /**
     * Zakazuje zobrazení tooltipu s validační chybou
     */
    public errorTooltipDisabled = false;

    public currentValidity: IValidationResult = null;

    public isInRequiredInvalidState: boolean = false;

    //protected shadowRoot: HTMLBodyElement;
    protected _innerElement: HTMLElement;
    protected _errorElement: HTMLElement;
    protected _eventGroup: string;
    protected _defaultValue: string = null;
    protected _multiline: boolean;

    private __focused: boolean;
    private _mouseOnElement: boolean;
    private _isValid: boolean;
    //private postponedRequired: boolean;
    //private recommended: boolean;
    //private required: boolean;
    //private requiredType: RequiredType = RequiredType.None;

    //#region Properties
    // bohuzel getter i setter propety musí mít stejnou viditelnost :/
    public get isFocused(): boolean {
        return this.__focused;
    }

    private set _focused(val: boolean) {
        this.classList.toggle("focused", val);
        this.__focused = val;
    }

    public get multiline(): boolean {
        return this._multiline;
    }

    public set multiline(value: boolean) {
        this._multiline = value;
    }

    public get value(): any {
        return "";
    }

    public set value(_v: any) {
        logger.error("Not implemented");
    }

    public get displayedValue(): ITextBuilder {
        if (this._innerElement instanceof HTMLInputElement)
            return this._innerElement.value;

        return this.value;
    }

    public set displayedValue(v: ITextBuilder) {
        if (this._innerElement instanceof HTMLInputElement)
            this._innerElement.value = v ? v.toString() : "";

        this.value = v;
    }

    public set isEditing(value: boolean) {
        if (value)
            this.setAttribute("isediting", value.toString());
        else
            this.removeAttribute("isediting");
    }

    public get isEditing(): boolean {
        const r = this.getAttribute("isediting") as string;

        return DataConverter.toBoolean(r, false);
    }

    public get isValid(): boolean {
        return this._isValid;
    }

    protected _setIsValid(val: boolean): void {
        this.classList.toggle("invalid", !val);
        this._isValid = val;
    }

    public override set enabled(value: boolean) {
        if (!value)
            this.setAttribute("enabled", value.toString());
        else
            this.removeAttribute("enabled");
    }

    public override get enabled(): boolean {
        const r = this.getAttribute("enabled") as string;

        return DataConverter.toBoolean(r, true);
    }

    public override set visible(value: boolean) {
        this._visible = value;
        const dispvalue = value ? "" : "none";

        // Nechceme skrývat i buňku v seznamu, ujedou pak sloupce (11380)
        //if (this.parentElement)
        //    this.parentElement.style.display = dispvalue;

        this.style.display = dispvalue;
        this.callEvent("visibility", { value: value } as IBooleanDetailValueEventData);
    }

    public override get visible(): boolean {
        return this._visible;
    }

    //#region PostponedRequired
    public set postponedRequired(value: boolean) {
        if (value)
            this.setAttribute("PostponedRequired", value.toString());
        else
            this.removeAttribute("PostponedRequired");
    }

    public get postponedRequired(): boolean {
        //return this.postponedRequired;
        const r = this.getAttribute("PostponedRequired") as string;

        return DataConverter.toBoolean(r, false);
    }
    //#endregion PostponedRequired

    //#region Recommended
    public set recommended(value: boolean) {
        if (value)
            this.setAttribute("recommended", value.toString());
        else
            this.removeAttribute("recommended");
    }

    public get recommended(): boolean {
        //return this.recommended;
        const r = this.getAttribute("recommended") as string;

        return DataConverter.toBoolean(r, false);
    }
    //#endregion Recommended

    //#region Required
    public set required(value: boolean) {
        if (value)
            this.setAttribute("required", value.toString());
        else
            this.removeAttribute("required");
        //this.setAttribute("required", value.toString());

        //this.required = value;
    }

    public get required(): boolean {
        //return this.required;
        const r = this.getAttribute("required") as string;

        return DataConverter.toBoolean(r, false);
    }

    public get defaultValue(): string {
        return this._defaultValue;
    }

    public set defaultValue(v: string) {
        this._defaultValue = v;
    }

    protected static override get observedAttributes(): string[] {
        return ["id", "required", "recommended", "setposponedReq", "enabled", "isediting"].concat(Control.observedAttributes);
    }
    public attrName: string;
    //#endregion Required

    //#endregion

    protected override _constructor(): void {
        super._constructor();

        this.classList.add("EditableControl");
        this.tabIndex = -1;
        //this.shadowRoot = (this as any).createShadowRoot();
        this._eventGroup = `editableControl_${this._innerId}_events`; //`
        this.isGuiValid = true;
        this._isValid = true;
        this._visible = true;
        this._focused = false;
        this.errorTooltipDisabled = false;
        this._errorElement = document.createElement("div");
        this._errorElement.classList.add("ErrorElement", "errDiv");
        this._errorElement.style.display = "none";
    }

    protected _focusInHandler(_e: Event): void {
        this._focused = true;
    }

    protected _focusOutHandler(): void {
        this._focused = false;
    }

    // You can also define the other lifecycle methods.
    protected override connectedCallback(): void {
        super.connectedCallback();

        if (this._innerElement) {
            Control.registerEventListener(this._eventGroup, this._innerElement, "change", (e) => {
                e.stopPropagation();
                this._afterChange();
            });

            Control.registerEventListener(this._eventGroup, this as HTMLElement, "focusin", this._focusInHandler);

            Control.registerEventListener(this._eventGroup, this as HTMLElement, "focusout", this._focusOutHandler);

            Control.registerEventListener(this._eventGroup, this._innerElement, "mouseenter", (_e: MouseEvent) => {
                this._mouseOnElement = true;
                this.showErrorPopup();
            });

            Control.registerEventListener(this._eventGroup, this._innerElement, "mouseleave", (_e: MouseEvent) => {
                this._mouseOnElement = false;
                if (this.errorPopup)
                    this.errorPopup.setVisibility(false);
            });
        }
    }

    protected override disconnectedCallback(): void {
        super.disconnectedCallback();
        Control.unregisterEventListener(this._eventGroup);
        if (this.errorPopup) {
            this.errorPopup.close();
            this.errorPopup = null;
        }
    }

    protected override attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        super.attributeChangedCallback(attrName, oldVal, newVal);
        let nVal: boolean;
        let oVal: boolean;

        switch (attrName) {
            case "required":
                nVal = DataConverter.toBoolean(newVal, false);
                this._setRequired(nVal);
                break;
            case "recommended":
                nVal = DataConverter.toBoolean(newVal, false);
                this._setRecommended(nVal);
                break;
            case "setposponedReq":
                nVal = DataConverter.toBoolean(newVal, false);
                this._setPosponedReq(nVal);
                break;
            case "enabled":
                nVal = DataConverter.toBoolean(newVal, true);
                oVal = DataConverter.toBoolean(oldVal, null);
                this._setEnabled(oVal, nVal);
                break;
            case "isediting":
                nVal = DataConverter.toBoolean(newVal, false);
                oVal = DataConverter.toBoolean(oldVal, null);
                this._setIsEditing(oVal, nVal);
                break;
        }
    }

    private _setIsEditing(oldVal: boolean, value: boolean): void {
        this._evaluateActualState();
        if (oldVal !== value)
            this.callEvent("isediting", { value: value } as IBooleanDetailValueEventData);
    }

    private _setRequired(_value: boolean): void {
        // Prázdná metoda
    }

    private _setRecommended(_value: boolean): void {
        // Prázdná metoda
    }

    private _setPosponedReq(_value: boolean): void {
        // Prázdná metoda
    }

    private _setEnabled(oldVal: boolean, value: boolean): void {
        let actualizeVisualState = false;
        if (this._visible && this.isEditing && ((oldVal == undefined || !oldVal) && value))
            actualizeVisualState = true;

        if (oldVal !== value) {
            this.callEvent("enabled", { value: value } as IBooleanDetailValueEventData);

            actualizeVisualState = true;
        }

        if (actualizeVisualState)
            this._evaluateActualState();
    }

    private _evaluateActualState(): void {
        const enable = this.enabled;

        this._setEnabledInput(enable);
        this.classList.toggle("Enabled", enable);
        this.classList.toggle("Disabled", !enable);

        this._evaluateActualStateInner();
    }

    /**
     * Propagace nastaveni na vnitrni EdiotableControls - napr. slozene editacni prvky (DateTime)
     */
    private _evaluateActualStateInner(): void {
        const innerEditableControls = Array.from(this.querySelectorAll(".EditableControl")) as EditableControl[];
        innerEditableControls.forEach((editableControl) => {
            editableControl.enabled = this.enabled;
            editableControl.isEditing = this.isEditing;
            if (typeof (editableControl["_evaluateActualState"]) === "function")
                editableControl._evaluateActualState();
        });
    }

    protected _setEnabledInput(value: boolean): void {
        if (this._innerElement) {
            if (this._innerElement && ("readOnly" in this._innerElement))
                (this._innerElement as any)["readOnly"] = !value;
            else if (this._innerElement && ("disabled" in this._innerElement))
                (this._innerElement as any)["disabled"] = !value;
        } else { // je potreba pokud neni jen jeden input v InnerElement
            const inputElements = Array.from(this.querySelectorAll("input")) as HTMLElement[];
            inputElements.forEach((input) => {
                if (input && ("readOnly" in input))
                    (input as any)["readOnly"] = !value;
                else if (input && ("disabled" in input))
                    (input as any)["disabled"] = !value;
            });
        }
    }

    /**
     * Metoda, která se volá po změně hodnoty v editovatelném prvku
     */
    protected _afterChange(): void {
        this.validate();
    }

    public validate(_forced: boolean = false): boolean {
        this.setValid();
        return this.isValid;
    }

    //Pokud byla kompnenta ve stavu Invalid tak ji vradi do stavu Valid
    public setValid(): void {
        this._setIsValid(true);

        if (this.errorPopup)
            this.errorPopup.setVisibility(false);

        this.validityChanged.trigger(this, { valid: true });
    }

    //Pokud zadam nevalidni hodnotu tak prepne komponentu do stavu Invalid
    public setInvalid(locKey: string, message: string = null): void {
        this._setIsValid(false);

        // OW.Remoting.Localization.addLocTxtData(this.ErrorElement, locKey, Localization.TEXT_CONTENT, message);
        if (locKey)
            this._errorElement.textContent = ValidityStateHelper.validityLocalizer.getLocalization(locKey);
        else if (message)
            this._errorElement.textContent = message;
        else
            this._errorElement.textContent = ValidityStateHelper.validityLocalizer.getLocalization("VAL-0");

        this.showErrorPopup();

        this.validityChanged.trigger(this, { valid: false, invalidValue: this.displayedValue, invalidLocKey: locKey, invalidMessage: message });
    }

    /**
     * Vytvoreni pickeru na vybirani hodnoty (pouziva napr. DateEdit pro ikonku kalendare)
     */
    protected _createValuePicker(): HTMLElement {
        return createElement({ type: "button", classes: "valuepicker" }) as HTMLInputElement;
    }

    /** Nastavi focus na input */
    public override focus(options?: FocusOptions): void {
        if (this._innerElement && ("focus" in this._innerElement)) {
            this._innerElement.focus(options);
        }
        //this.IsInRequiredInvalidState = false;
    }

    /**
     * Zobrazeni elementu s chybou nad/pod dle dostupneho prostoru. Pokud neni dostatek prostoru nad ani pod, tak se zobrazi dole.
     * @param popUpMenu - dropDown menu, ktere se bude vykreslovat
     * @param popUpElemenet - element do ktereho se vykresluje obsah pupupOkna
     * @param width - preddefinovana defaultni sirka. Pouziva se u LOV, protoze grid ma pred doctenim nulovou vysku a drop down menu se zobrazovalo jen jako tecka
     * @param height - preddefinovana defaultni vyska. Pouziva se u LOV, protoze grid ma pred doctenim nulovou vysku a drop down menu se zobrazovalo jen jako tecka
     */
    public dropDownElementPosition(popUpMenu: PopupMenu, popUpElemenet: HTMLElement, gridRect: ClientRect = null, width: number = null, height: number = null): void {
        if (popUpElemenet != undefined && popUpMenu)
            popUpMenu.arangePopupToEditableControl(popUpElemenet, this as HTMLElement, this.isFocused, gridRect, width, height);
    }

    public showErrorPopup(): void {
        // nemá význam zobrazovat tooltip s chybou pokud je komponenta validní nebo má tooltipy zakázané nebo nemá nad sebou myš
        if (this.isValid || this.errorTooltipDisabled || !this._mouseOnElement)
            return;

        if (!this.errorPopup) {
            this.errorPopup = new PopupMenu(this._errorElement, false, null, null, null, null, false);
        }

        this.dropDownElementPosition(this.errorPopup, this._errorElement);
        this._errorElement.style.display = "block";
        this.errorPopup.setVisibility(true);
    }

    /**
     * Vrátí aktuální pozici kurzoru v zadaném elementu
     * @param el Element, ve kterém se vyhledavá pozice kurzoru
     */
    public static getInputSelection(el: HTMLInputElement): { start: number; end: number } {
        let start = 0;
        let end = 0;

        if (typeof el.selectionStart === "number" && typeof el.selectionEnd === "number") {
            start = el.selectionStart;
            end = el.selectionEnd;
        }

        return {
            start: start,
            end: end,
        };
    }

    /**
     * Nastavi pozici kurzoru na dane misto
     * @link http://stackoverflow.com/questions/1865563/set-cursor-at-a-length-of-14-onfocus-of-a-textbox/1867393#1867393
     */
    public static setCaret(node: HTMLInputElement, pos: number): boolean {
        if (node.setSelectionRange) {
            node.setSelectionRange(pos, pos);
            return true;
        }
        return false;
    }
}

registerCustomElement("tsc-editablecontrol", EditableControl);
